// src/lib/services/shippingService.ts
// Single source of truth for a shop's shipping rules.
//
// Three-layer model:
//   1. Baseline (Shop): shippingFeeTransfer + shippingFeeCOD — flat fees per
//      payment method. Set at /dashboard/bank-settings (การชำระเงิน).
//   2. Promotion override (FREE_SHIPPING): if minPurchase is met, fee → 0.
//      The promotion's discountValue is no longer authoritative for the
//      baseline fee — it's kept for legacy/display fallback only.
//   3. Per-product solo override (Product.freeShippingAlone): when the order
//      contains ONLY that product (any quantity / variant), fee → 0. Any
//      other product in the cart → baseline rules apply. Pass the order's
//      items to computeShippingFee to activate this layer.
//   4. Area override (AreaShippingFee, gated by Shop.areaShippingEnabled):
//      ค่าส่งพิเศษราย จังหวัด/อำเภอ/ตำบล — เมื่อเปิดใช้และที่อยู่ลูกค้าตรง
//      พื้นที่ที่ตั้งไว้ ค่าส่งใช้ราคาพื้นที่นั้นแทน baseline (แต่ยังแพ้ทุก
//      เงื่อนไขส่งฟรีข้อ 2-3 และไม่ใช้กับ PICKUP/RIDER ที่ไม่มีการจัดส่ง).
//      Pass the customer's address to computeShippingFee to activate.

import { prisma } from '../prisma';
import type { PromotionStatus, PaymentMethod } from '@prisma/client';

export interface ShippingPolicy {
  /** Baseline shipping fee for TRANSFER orders (THB). */
  feeTransfer: number;
  /** Baseline shipping fee for COD orders (THB). */
  feeCOD: number;
  /** Fee for store-pickup orders (THB) — usually 0 (no delivery). */
  feePickup: number;
  /** Fee for customer-rider-pickup orders (THB) — usually 0. */
  feeRider: number;
  /** Threshold above which shipping becomes free (any payment method),
   *  or null if no FREE_SHIPPING promotion is active. */
  freeShippingMin: number | null;
  /** Piece-count threshold (Σ quantity×unitCount) at/above which shipping
   *  becomes free, or null if the active FREE_SHIPPING promo has no count
   *  rule. Counts real pieces so a "set" product (unitCount > 1) contributes
   *  its full piece count toward the threshold. */
  freeShippingMinQty: number | null;
  /** True if both baseline fees are 0 — every order ships free regardless. */
  alwaysFree: boolean;
  /** The active FREE_SHIPPING promotion that drives the override, if any. */
  promotion: ActiveShippingPromotion | null;
  /** Products with freeShippingAlone enabled — an order of ONLY one of
   *  these (any quantity) ships free. */
  soloFreeProducts: Array<{ id: string; name: string }>;
  /** ร้านเปิดใช้ค่าส่งพิเศษรายพื้นที่หรือไม่ (Shop.areaShippingEnabled). */
  areaShippingEnabled: boolean;
  /** กติกาค่าส่งรายพื้นที่ (ว่างเมื่อปิดใช้ — จะได้ไม่ match อะไรเลย). */
  areaFees: AreaFeeRule[];
  /** ข้อความ popup บนเซลเพจสำหรับพื้นที่ ASK (ร้านแก้ได้ — ว่างใช้ค่ามาตรฐาน). */
  areaAskMessage: string;
  /** Human-readable Thai summary suitable to drop into an AI system prompt. */
  summary: string;
}

/** ประเภทพื้นที่: FIXED = ระบุค่าส่งได้ · ASK = ระบุไม่ได้ ต้องให้แอดมินยืนยัน */
export type AreaShippingKind = 'FIXED' | 'ASK';

/** กติกาค่าส่งรายพื้นที่ 1 แถว — ตรงทั้ง 3 ชั้น (จังหวัด+อำเภอ+ตำบล) ถึงใช้ fee นี้ */
export interface AreaFeeRule {
  province: string;
  district: string;
  subdistrict: string;
  /** ค่าส่ง (บาท) — พื้นที่ ASK เป็น 0 เสมอ (ไม่มีความหมาย) */
  fee: number;
  kind: AreaShippingKind;
}

/** ข้อความมาตรฐานของ popup พื้นที่ ASK (requirement 2026-09-07) — ร้านแก้ได้ที่หน้าตั้งค่า */
export const DEFAULT_AREA_ASK_MESSAGE =
  'พื้นที่นี้เป็นพื้นที่จัดส่งพิเศษ และต้องตรวจสอบค่าจัดส่ง\n\nกรุณาติดต่อแอดมินเพื่อยืนยันค่าจัดส่งก่อนดำเนินการชำระเงิน';

/** ที่อยู่ลูกค้าเท่าที่รู้ — ส่วนไหนไม่รู้ปล่อย null ได้ (จะไม่ match พื้นที่ไหนเลย) */
export interface AreaShippingAddress {
  province?: string | null;
  district?: string | null;
  subdistrict?: string | null;
}

export interface ActiveShippingPromotion {
  id: string;
  name: string;
  description: string | null;
  minPurchase: number | null;
  minQuantity: number | null;
  endDate: Date | null;
}

/**
 * Get the effective shipping policy for `shopId`.
 *
 * Reads baseline fees from Shop, then layers the most recent active
 * FREE_SHIPPING promotion's minPurchase as an override threshold.
 */
export async function getShippingPolicy(shopId: string): Promise<ShippingPolicy> {
  const now = new Date();

  const [shop, promo, soloFreeProducts, areaRows] = await Promise.all([
    prisma.shop.findUnique({
      where: { id: shopId },
      select: {
        shippingFeeTransfer: true, shippingFeeCOD: true, shippingFeePickup: true, shippingFeeRider: true,
        areaShippingEnabled: true, areaAskMessage: true,
        // วิธีชำระที่ร้านเปิด — ใช้กรองบรรทัดค่าส่งใน summary (ที่วิ่งเข้า prompt)
        // ร้านที่ปิด COD เคยยังเห็น "ปลายทาง: 50฿" ค้างอยู่ใน prompt แล้ว AI
        // เอาไปเสนอลูกค้าต่อ (ร้านตุ๋นตุ๋นๆ 2026-09-10)
        acceptCOD: true, acceptTransfer: true,
      },
    }),
    prisma.promotion.findFirst({
      where: {
        shopId,
        type: 'FREE_SHIPPING',
        status: 'ACTIVE' satisfies PromotionStatus,
        OR: [{ startDate: null }, { startDate: { lte: now } }],
        AND: [{ OR: [{ endDate: null }, { endDate: { gte: now } }] }],
      },
      orderBy: { createdAt: 'desc' },
    }),
    prisma.product.findMany({
      where: { shopId, isActive: true, freeShippingAlone: true },
      select: { id: true, name: true },
    }),
    prisma.areaShippingFee.findMany({
      where: { shopId },
      select: { province: true, district: true, subdistrict: true, fee: true, kind: true },
    }),
  ]);

  const feeTransfer = shop ? Number(shop.shippingFeeTransfer) : 0;
  const feeCOD = shop ? Number(shop.shippingFeeCOD) : 0;
  const feePickup = shop ? Number(shop.shippingFeePickup) : 0;
  const feeRider = shop ? Number(shop.shippingFeeRider) : 0;
  const alwaysFree = feeTransfer === 0 && feeCOD === 0 && feePickup === 0 && feeRider === 0;

  const freeShippingMin = promo?.minPurchase != null ? Number(promo.minPurchase) : null;
  const freeShippingMinQty = promo?.minQuantity != null ? Number(promo.minQuantity) : null;

  const promotion: ActiveShippingPromotion | null = promo
    ? {
        id: promo.id,
        name: promo.name,
        description: promo.description,
        minPurchase: freeShippingMin,
        minQuantity: freeShippingMinQty,
        endDate: promo.endDate,
      }
    : null;

  // ปิดสวิตช์ = เก็บกติกาเป็นลิสต์ว่าง เพื่อให้ downstream ไม่ต้องเช็ค 2 ชั้น
  const areaShippingEnabled = shop?.areaShippingEnabled === true;
  const areaFees: AreaFeeRule[] = areaShippingEnabled
    ? areaRows.map((r) => ({
        province: r.province,
        district: r.district,
        subdistrict: r.subdistrict,
        fee: Number(r.fee),
        kind: r.kind,
      }))
    : [];
  const areaAskMessage = shop?.areaAskMessage?.trim() || DEFAULT_AREA_ASK_MESSAGE;

  return {
    feeTransfer,
    feeCOD,
    feePickup,
    feeRider,
    freeShippingMin,
    freeShippingMinQty,
    alwaysFree,
    promotion,
    soloFreeProducts,
    areaShippingEnabled,
    areaFees,
    areaAskMessage,
    summary: buildSummary(
      feeTransfer, feeCOD, freeShippingMin, freeShippingMinQty, promotion?.name ?? null,
      alwaysFree, soloFreeProducts, areaFees,
      { cod: shop?.acceptCOD !== false, transfer: shop?.acceptTransfer !== false },
    ),
  };
}

function buildSummary(
  feeTransfer: number,
  feeCOD: number,
  freeShippingMin: number | null,
  freeShippingMinQty: number | null,
  promoName: string | null,
  alwaysFree: boolean,
  soloFreeProducts: Array<{ id: string; name: string }> = [],
  areaFees: AreaFeeRule[] = [],
  /** วิธีชำระที่ร้านเปิด — ปิดอันไหนไม่ต้องโชว์ค่าส่งของอันนั้น */
  accepted: { cod: boolean; transfer: boolean } = { cod: true, transfer: true },
): string {
  // ค่าส่งรายพื้นที่ — บอก AI แค่ว่า "มี" และระบบคิดให้เองตามที่อยู่ ไม่ยัด
  // ทุกตำบลลง prompt (ร้านตั้งได้หลายสิบแถว) AI ไม่ควร quote ราคาพื้นที่เอง
  const fixedAreaCount = areaFees.filter((a) => a.kind !== 'ASK').length;
  const areaNote =
    fixedAreaCount > 0
      ? ` | 📍 มีค่าส่งพิเศษรายพื้นที่ ${fixedAreaCount} พื้นที่ (ระบบคิดให้อัตโนมัติตามตำบล/อำเภอ/จังหวัดของลูกค้า — ค่าส่งจริงอาจต่างจากอัตราปกติ)`
      : '';
  // กฎส่งฟรีรายสินค้า — ต่อท้าย summary ทุกกรณี (ยกเว้น alwaysFree ซึ่ง
  // ฟรีอยู่แล้ว) เพื่อให้ AI อธิบายเงื่อนไขกับลูกค้าได้ถูก: ฟรีเฉพาะตอน
  // ออเดอร์มีสินค้านั้นล้วนๆ มีตัวอื่นปนคิดปกติ
  const soloNote =
    soloFreeProducts.length > 0
      ? ` | 🚚 ส่งฟรีพิเศษ: ${soloFreeProducts.map((p) => `"${p.name}"`).join(', ')} — ฟรีเฉพาะเมื่อออเดอร์มีสินค้านี้อย่างเดียวล้วนๆ (กี่ชิ้นก็ได้) ถ้ามีสินค้าอื่นร่วมด้วยคิดค่าส่งปกติ`
      : '';

  if (alwaysFree) return 'ส่งฟรีทุกออเดอร์ (ไม่มีค่าจัดส่งทุกวิธีชำระ)';

  // ร้านปิดวิธีไหน = บรรทัดค่าส่งของวิธีนั้นหายไปจาก summary ทั้งก้อน ไม่ใช่แค่
  // ไม่ให้เสนอ — เพราะ summary นี้วิ่งเข้า prompt ตรง ๆ ("ค่าจัดส่ง — โอน: 40฿ ·
  // ปลายทาง: 50฿") AI อ่านแล้วสรุปให้ลูกค้าว่ามีปลายทางทั้งที่ร้านปิดไปแล้ว
  const feeLines = [
    accepted.transfer ? (feeTransfer === 0 ? 'โอน: ฟรี' : `โอน: ${feeTransfer.toLocaleString()}฿`) : null,
    accepted.cod ? (feeCOD === 0 ? 'ปลายทาง: ฟรี' : `ปลายทาง: ${feeCOD.toLocaleString()}฿`) : null,
  ].filter(Boolean) as string[];
  // เปิดแต่รับหน้าร้าน/ไรเดอร์ — ไม่มีค่าส่งพัสดุให้ quote
  const feeText = feeLines.length > 0 ? feeLines.join(' · ') : 'ตามวิธีรับสินค้าที่ร้านเปิด';

  // โปรส่งฟรีตั้งเกณฑ์ได้ทั้ง "ครบ X บาท" และ/หรือ "ครบ X ชิ้น" (นับชิ้นจริง)
  // — เข้าเงื่อนไขใดก็ได้ก็ส่งฟรี (OR). รวมเป็นข้อความเดียวเพื่อให้ AI quote ถูก.
  const freeConds: string[] = [];
  if (freeShippingMin != null && freeShippingMin > 0) freeConds.push(`ซื้อครบ ${freeShippingMin.toLocaleString()}฿`);
  if (freeShippingMinQty != null && freeShippingMinQty > 0) freeConds.push(`ครบ ${freeShippingMinQty.toLocaleString()} ชิ้น`);

  if (freeConds.length > 0 && promoName) {
    return `ค่าจัดส่ง — ${feeText} (ฟรีเมื่อ${freeConds.join(' หรือ ')} — โปร "${promoName}")${soloNote}${areaNote}`;
  }
  if (freeShippingMin === 0 && promoName) {
    return `ส่งฟรีทุกออเดอร์ (โปร "${promoName}")`;
  }
  return `ค่าจัดส่ง — ${feeText}${soloNote}${areaNote}`;
}

// ─── ค่าส่งพิเศษรายพื้นที่ ─────────────────────────────────────────────────

/**
 * Normalize ชื่อพื้นที่ก่อนเทียบ — ตัดคำนำหน้า (ตำบล/แขวง/อำเภอ/เขต/จังหวัด
 * และตัวย่อ ต./อ./จ.) + ช่องว่าง/ตัวพิมพ์ เพื่อให้ "ต.สีลม" ที่ AI แกะจากแชท
 * ตรงกับ "สีลม" ที่ร้านพิมพ์ในหน้าตั้งค่า (และกลับกัน). กทม. ใช้ เขต/แขวง
 * ก็ตัดเหมือนกัน จึงไม่ต้องตั้งแยก.
 */
function normalizeAreaPart(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw
    .trim()
    .replace(/^(ตำบล|แขวง|อำเภอ|เขต|จังหวัด|ต\.|อ\.|จ\.)\s*/u, '')
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

/**
 * หา fee ของพื้นที่ที่ตรงกับที่อยู่ลูกค้า — ต้องตรงครบทั้ง 3 ชั้น
 * (จังหวัด+อำเภอ+ตำบล หลัง normalize). คืน null เมื่อปิดใช้ / ที่อยู่ยังไม่ครบ /
 * ไม่ตรงพื้นที่ไหนเลย → ผู้เรียกตกไปใช้ค่าส่งปกติ.
 */
export function resolveAreaShippingFee(
  policy: ShippingPolicy,
  address?: AreaShippingAddress | null,
): number | null {
  const rule = resolveAreaShippingRule(policy, address);
  return rule && rule.kind === 'FIXED' ? rule.fee : null;
}

/**
 * หา "แถวพื้นที่" ที่ตรงกับที่อยู่ลูกค้า (ทั้ง FIXED และ ASK) — ผู้เรียกที่สนใจ
 * พื้นที่ ASK (เซลเพจ: ไม่คิดค่าส่ง + popup + ปักธงรอยืนยัน) ใช้ตัวนี้.
 * ฝั่งแชท/AI ยังใช้ resolveAreaShippingFee ซึ่งมองข้าม ASK (คิดค่าส่งปกติตามเดิม)
 */
export function resolveAreaShippingRule(
  policy: ShippingPolicy,
  address?: AreaShippingAddress | null,
): AreaFeeRule | null {
  if (!policy.areaShippingEnabled || policy.areaFees.length === 0 || !address) return null;
  const province = normalizeAreaPart(address.province);
  const district = normalizeAreaPart(address.district);
  const subdistrict = normalizeAreaPart(address.subdistrict);
  if (!province || !district || !subdistrict) return null;
  for (const rule of policy.areaFees) {
    if (
      normalizeAreaPart(rule.province) === province &&
      normalizeAreaPart(rule.district) === district &&
      normalizeAreaPart(rule.subdistrict) === subdistrict
    ) {
      return rule;
    }
  }
  return null;
}

/**
 * ดึงที่อยู่ (จังหวัด/อำเภอ/ตำบล) ของออเดอร์สำหรับคิดค่าส่งรายพื้นที่ —
 * ตัวช่วยสำหรับ call site ที่ select ออเดอร์มาโดยไม่มี field ที่อยู่ติดมือ.
 * คืน null เมื่อไม่พบออเดอร์ (fee จะตกไปใช้ baseline ซึ่งปลอดภัย).
 */
export async function getOrderAreaAddress(orderId: string): Promise<AreaShippingAddress | null> {
  const row = await prisma.order.findUnique({
    where: { id: orderId },
    select: { customerProvince: true, customerDistrict: true, customerSubdistrict: true },
  });
  if (!row) return null;
  return {
    province: row.customerProvince,
    district: row.customerDistrict,
    subdistrict: row.customerSubdistrict,
  };
}

/**
 * เช็คเงื่อนไข "ส่งฟรีเมื่อสั่งสินค้านี้อย่างเดียว" — เข้าเงื่อนไขเมื่อทุก
 * line item ในออเดอร์เป็นสินค้าตัวเดียวกัน (กี่ชิ้น/กี่ variant ก็ได้)
 * และตัวนั้นเปิด freeShippingAlone ไว้
 */
export function isSoloFreeShipping(
  policy: ShippingPolicy,
  items?: ShippingItem[] | null,
): boolean {
  if (!items || items.length === 0 || policy.soloFreeProducts.length === 0) return false;
  const ids = new Set(items.map((i) => i.productId));
  if (ids.size !== 1) return false;
  const only = items[0].productId;
  return !!only && policy.soloFreeProducts.some((p) => p.id === only);
}

/** A cart line for shipping math. `quantity` × `unitCount` = real pieces;
 *  both default to 1 when absent so callers that only have productId still
 *  work for the solo-free rule. */
export interface ShippingItem {
  productId: string | null;
  quantity?: number | null;
  unitCount?: number | null;
}

/** Total real pieces in the cart = Σ(quantity × unitCount), defaulting each
 *  factor to 1. Drives the "ส่งฟรีเมื่อครบ X ชิ้น" promotion threshold. */
export function countOrderPieces(items?: ShippingItem[] | null): number {
  if (!items || items.length === 0) return 0;
  return items.reduce(
    (sum, it) => sum + Math.max(1, Number(it.quantity ?? 1)) * Math.max(1, Number(it.unitCount ?? 1)),
    0,
  );
}

/**
 * Compute the final shipping fee for a given subtotal + payment method.
 * Used by checkout flows.
 *
 *   - alwaysFree → 0
 *   - order is ONLY a freeShippingAlone product (pass `items`) → 0
 *   - subtotal ≥ freeShippingMin → 0 (promotion override, baht-based)
 *   - total pieces ≥ freeShippingMinQty → 0 (promotion override, count-based;
 *     needs `items` carrying quantity + unitCount)
 *   - else baseline fee for the chosen payment method
 *
 * If `paymentMethod` is null/undefined, returns the higher of the two as a
 * conservative pre-choice estimate (so AI doesn't quote the cheaper one and
 * surprise the customer later).
 *
 * `items` is optional for backward compat, but every caller that has the
 * order's line items SHOULD pass them — without it the per-product solo
 * free-shipping rule and the count-based free-ship threshold can't apply.
 */
export function computeShippingFee(
  policy: ShippingPolicy,
  subtotal: number,
  paymentMethod?: PaymentMethod | null,
  items?: ShippingItem[] | null,
  /** ที่อยู่ลูกค้า (ถ้ารู้) — เปิดใช้ layer ค่าส่งพิเศษรายพื้นที่. ไม่ส่ง =
   *  ใช้ค่าส่งปกติเหมือนเดิม (backward compat). */
  address?: AreaShippingAddress | null,
): number {
  if (policy.alwaysFree) return 0;
  if (isSoloFreeShipping(policy, items)) return 0;
  if (policy.freeShippingMin != null && subtotal >= policy.freeShippingMin) return 0;
  if (policy.freeShippingMinQty != null && policy.freeShippingMinQty > 0 &&
      countOrderPieces(items) >= policy.freeShippingMinQty) return 0;
  // ค่าส่งรายพื้นที่ — แทน baseline เฉพาะออเดอร์ที่มีการจัดส่งจริง
  // (PICKUP/RIDER ไม่มีการส่งพัสดุ ใช้ fee ของวิธีนั้นตามเดิม)
  if (paymentMethod !== 'PICKUP' && paymentMethod !== 'RIDER') {
    const areaFee = resolveAreaShippingFee(policy, address);
    if (areaFee != null) return areaFee;
  }
  if (paymentMethod === 'COD') return policy.feeCOD;
  if (paymentMethod === 'TRANSFER') return policy.feeTransfer;
  if (paymentMethod === 'PICKUP') return policy.feePickup;
  if (paymentMethod === 'RIDER') return policy.feeRider;
  return Math.max(policy.feeTransfer, policy.feeCOD);
}

/**
 * Whether an order paid via `method` needs a shipping address.
 * รับหน้าร้าน (PICKUP) และ ไรเดอร์ (RIDER) ไม่ต้องใช้ที่อยู่ — ขอแค่ชื่อ+เบอร์.
 * null/COD/TRANSFER ยังต้องใช้ที่อยู่ตามปกติ.
 */
export function paymentNeedsAddress(method: string | null | undefined): boolean {
  return method !== 'PICKUP' && method !== 'RIDER';
}
